import React, { useMemo, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Switch,
  ActivityIndicator,
  SafeAreaView,
  Alert,
  Linking,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { format } from "date-fns";
import {
  OpportunityType,
  GradeLevel,
  InteractionType,
} from "../../types";
import { useAuth } from "../../contexts/AuthContext";
import {
  approveCrawledOpportunity,
  rejectCrawledOpportunity,
} from "../../services/parseService";

interface CrawledOpportunityEditorProps {
  item: any; // Parse object or plain record from getCrawledOpportunities
  onClose: () => void;
  onProcessed: () => void; // called after a successful approve/reject (reload + close)
}

const opportunityTypes: OpportunityType[] = [
  "Competition",
  "Grant",
  "Scholarship",
  "Hackathon",
  "Internship",
  "Workshop/Programs",
  "Conference",
  "Other",
];

const gradeOptions: GradeLevel[] = [
  "Elementary",
  "Middle School",
  "High School",
  "Undergraduate",
  "Graduate",
  "Professional",
  "All Levels",
];

const interactionOptions: InteractionType[] = ["In-Person", "Virtual", "Hybrid"];

// Read a field whether `item` is a Parse object (.get) or a plain object.
const read = (item: any, key: string) => (item?.get ? item.get(key) : item?.[key]);

// CrawledOpportunity stores program/application dates as plain strings. Guard against the
// rare case where a value arrives as a Parse Date object instead.
const asDateString = (v: any): string => {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (v instanceof Date) return isNaN(v.getTime()) ? "" : `${v.getMonth() + 1}/${v.getDate()}/${v.getFullYear()}`;
  if (typeof v === "object" && v.iso) {
    const d = new Date(v.iso);
    return isNaN(d.getTime()) ? "" : `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  }
  return "";
};

const normalizeTypeArray = (v: any): OpportunityType[] => {
  if (Array.isArray(v)) return v as OpportunityType[];
  if (typeof v === "string") {
    return v
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean) as OpportunityType[];
  }
  return [];
};

const CrawledOpportunityEditor: React.FC<CrawledOpportunityEditorProps> = ({
  item,
  onClose,
  onProcessed,
}) => {
  const { currentUser } = useAuth();
  const id: string = item.id || item.objectId || read(item, "objectId");

  // Snapshot of what the crawler extracted — used to pre-fill the form and to diff edits.
  const original = useMemo(
    () => ({
      title: (read(item, "title") || "").toString().trim(),
      description: (read(item, "description") || "").toString().trim(),
      type: normalizeTypeArray(read(item, "type")),
      organization: (read(item, "organization") || "").toString().trim(),
      location: (read(item, "location") || "").toString().trim(),
      website: (read(item, "website") || "").toString().trim(),
      eligibility: (read(item, "eligibility") || "").toString().trim(),
      cost: (read(item, "cost") || "").toString().trim(),
      tags: Array.isArray(read(item, "tags")) ? (read(item, "tags") as string[]) : [],
      gradeLevel: Array.isArray(read(item, "gradeLevel")) ? (read(item, "gradeLevel") as GradeLevel[]) : [],
      interactionTypes: Array.isArray(read(item, "interactionTypes"))
        ? (read(item, "interactionTypes") as InteractionType[])
        : [],
      startDate: asDateString(read(item, "startDate")),
      endDate: asDateString(read(item, "endDate")),
      applicationOpenDate: asDateString(read(item, "applicationOpenDate")),
      applicationCloseDate: asDateString(read(item, "applicationCloseDate")),
      isYearlyRecurring: !!read(item, "isYearlyRecurring"),
    }),
    [item],
  );

  // Editable form state, pre-filled from the crawl.
  const [title, setTitle] = useState(original.title);
  const [description, setDescription] = useState(original.description);
  const [types, setTypes] = useState<OpportunityType[]>(original.type);
  const [organization, setOrganization] = useState(original.organization);
  const [location, setLocation] = useState(original.location);
  const [website, setWebsite] = useState(original.website);
  const [eligibility, setEligibility] = useState(original.eligibility);
  const [cost, setCost] = useState(original.cost);
  const [tags, setTags] = useState<string[]>(original.tags);
  const [tagInput, setTagInput] = useState("");
  const [gradeLevels, setGradeLevels] = useState<GradeLevel[]>(original.gradeLevel);
  const [interactionTypes, setInteractionTypes] = useState<InteractionType[]>(original.interactionTypes);
  const [startDate, setStartDate] = useState(original.startDate);
  const [endDate, setEndDate] = useState(original.endDate);
  const [applicationOpenDate, setApplicationOpenDate] = useState(original.applicationOpenDate);
  const [applicationCloseDate, setApplicationCloseDate] = useState(original.applicationCloseDate);
  const [isYearlyRecurring, setIsYearlyRecurring] = useState(original.isYearlyRecurring);

  const [isProcessing, setIsProcessing] = useState<null | "approve" | "reject">(null);
  const [showRaw, setShowRaw] = useState(false);

  // Provenance (read-only) — what the AI crawled.
  const confidence = read(item, "confidence");
  const dedupScore = read(item, "dedupScore");
  const dupHigh = typeof dedupScore === "number" && dedupScore >= 0.7;
  const sourceUrl = read(item, "sourceUrl") || read(item, "website");
  const sourceUrls: string[] = Array.isArray(read(item, "sourceUrls")) ? read(item, "sourceUrls") : [];
  const searchQuery = read(item, "searchQuery");
  const linkNeedsReview = !!read(item, "linkNeedsReview");
  const linkCandidates: string[] = Array.isArray(read(item, "linkCandidates")) ? read(item, "linkCandidates") : [];
  const pageType = read(item, "pageType");
  const linkVerified = !!read(item, "linkVerified");
  const linkResolution = read(item, "linkResolution");
  // Offer the link picker whenever the item came from a roundup/list (flagged OR auto-resolved, so an
  // admin can sanity-check a wrong auto-match) — but not for clean single-opportunity pages.
  const showLinkCandidates = linkCandidates.length > 0 && (linkNeedsReview || pageType === "listing");
  const crawledAtRaw = read(item, "crawledAt") || read(item, "createdAt");
  const rawExtract = read(item, "rawExtract");

  const crawledAtStr = useMemo(() => {
    try {
      const d = crawledAtRaw instanceof Date ? crawledAtRaw : crawledAtRaw ? new Date(crawledAtRaw) : null;
      return d && !isNaN(d.getTime()) ? format(d, "MMM d, yyyy") : "";
    } catch {
      return "";
    }
  }, [crawledAtRaw]);

  const rawPretty = useMemo(() => {
    if (!rawExtract) return "";
    if (typeof rawExtract !== "string") {
      try {
        return JSON.stringify(rawExtract, null, 2);
      } catch {
        return String(rawExtract);
      }
    }
    try {
      return JSON.stringify(JSON.parse(rawExtract), null, 2);
    } catch {
      return rawExtract;
    }
  }, [rawExtract]);

  const notify = (titleText: string, message: string) => {
    if (Platform.OS === "web") {
      // eslint-disable-next-line no-alert
      alert(message);
    } else {
      Alert.alert(titleText, message);
    }
  };

  const confirmReject = (): Promise<boolean> => {
    if (Platform.OS === "web") {
      // eslint-disable-next-line no-alert
      return Promise.resolve(window.confirm("Reject this crawled opportunity? It will be removed from the review queue."));
    }
    return new Promise((resolve) => {
      Alert.alert(
        "Reject opportunity",
        "It will be removed from the review queue.",
        [
          { text: "Cancel", style: "cancel", onPress: () => resolve(false) },
          { text: "Reject", style: "destructive", onPress: () => resolve(true) },
        ],
      );
    });
  };

  const openLink = (url?: string) => {
    if (!url) return;
    if (Platform.OS === "web") {
      window.open(url, "_blank");
    } else {
      Linking.openURL(url);
    }
  };

  const toggleType = (t: OpportunityType) =>
    setTypes((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  const toggleGrade = (g: GradeLevel) =>
    setGradeLevels((prev) => (prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]));
  const toggleInteraction = (i: InteractionType) =>
    setInteractionTypes((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]));

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) {
      setTags([...tags, t]);
      setTagInput("");
    }
  };
  const removeTag = (t: string) => setTags(tags.filter((x) => x !== t));

  // Diff current form values against the crawl; only changed fields are sent as edits.
  const collectEdits = (): Record<string, any> => {
    const current: Record<string, any> = {
      title: title.trim(),
      description: description.trim(),
      type: types,
      organization: organization.trim(),
      location: location.trim(),
      website: website.trim(),
      eligibility: eligibility.trim(),
      cost: cost.trim(),
      tags,
      gradeLevel: gradeLevels,
      interactionTypes,
      startDate: startDate.trim(),
      endDate: endDate.trim(),
      applicationOpenDate: applicationOpenDate.trim(),
      applicationCloseDate: applicationCloseDate.trim(),
      isYearlyRecurring,
    };
    const edits: Record<string, any> = {};
    Object.keys(current).forEach((key) => {
      if (JSON.stringify(current[key]) !== JSON.stringify((original as any)[key])) {
        edits[key] = current[key];
      }
    });
    return edits;
  };

  const handleApprove = async () => {
    if (!title.trim()) return notify("Missing information", "Please enter a title.");
    if (!organization.trim()) return notify("Missing information", "Please enter the organization name.");
    if (!location.trim()) return notify("Missing information", "Please enter a location.");
    if (types.length === 0) return notify("Missing information", "Please select at least one type.");
    if (linkNeedsReview && !website.trim()) {
      return notify(
        "Link needs review",
        "The crawler couldn't determine this opportunity's official link (it was found on a roundup/list page). Please set the Website URL before publishing.",
      );
    }

    try {
      setIsProcessing("approve");
      const edits = collectEdits();
      // Once the admin has supplied a link, clear the flag so the published item isn't marked as
      // unresolved and the queue badge disappears.
      if (linkNeedsReview && website.trim()) edits.linkNeedsReview = false;
      await approveCrawledOpportunity(id, currentUser?.id, Object.keys(edits).length ? edits : undefined);
      onProcessed();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      notify("Error", `Failed to approve & publish: ${message}`);
    } finally {
      setIsProcessing(null);
    }
  };

  const handleReject = async () => {
    const ok = await confirmReject();
    if (!ok) return;
    try {
      setIsProcessing("reject");
      await rejectCrawledOpportunity(id, currentUser?.id);
      onProcessed();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      notify("Error", `Failed to reject: ${message}`);
    } finally {
      setIsProcessing(null);
    }
  };

  const busy = isProcessing !== null;

  return (
    <View style={styles.fullScreenModal}>
      <SafeAreaView style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <TouchableOpacity style={styles.backButton} onPress={onClose} disabled={busy}>
              <Ionicons name="arrow-back" size={20} color="#6B7280" />
            </TouchableOpacity>
            <View style={styles.headerTitleContainer}>
              <Text style={styles.headerTitle} numberOfLines={1}>
                Review crawled opportunity
              </Text>
              <Text style={styles.headerSubtitle} numberOfLines={1}>
                Edit any field, then approve to publish
              </Text>
            </View>
          </View>
          <View style={styles.headerActions}>
            <TouchableOpacity
              style={[styles.rejectButton, busy && styles.disabledButton]}
              onPress={handleReject}
              disabled={busy}
              activeOpacity={0.7}
            >
              {isProcessing === "reject" ? (
                <ActivityIndicator size="small" color="#DC2626" />
              ) : (
                <Text style={styles.rejectButtonText}>Reject</Text>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.approveButton, busy && styles.disabledButton]}
              onPress={handleApprove}
              disabled={busy}
              activeOpacity={0.7}
            >
              {isProcessing === "approve" ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <>
                  <Ionicons name="checkmark" size={16} color="#FFFFFF" />
                  <Text style={styles.approveButtonText}>Approve &amp; Publish</Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </View>

        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.contentContainer}
          showsVerticalScrollIndicator={false}
        >
          {/* Provenance — what the AI crawled (read-only) */}
          <View style={styles.provenanceSection}>
            <View style={styles.provenanceHeader}>
              <Ionicons name="sparkles-outline" size={16} color="#7C3AED" />
              <Text style={styles.provenanceTitle}>What the crawler extracted</Text>
            </View>

            <View style={styles.badges}>
              {typeof confidence === "number" && (
                <View style={styles.badge}>
                  <Text style={styles.badgeText}>confidence {(confidence * 100).toFixed(0)}%</Text>
                </View>
              )}
              {typeof dedupScore === "number" && (
                <View style={[styles.badge, dupHigh && styles.badgeWarn]}>
                  <Text style={[styles.badgeText, dupHigh && styles.badgeWarnText]}>
                    similarity {(dedupScore * 100).toFixed(0)}%{dupHigh ? " ⚠ possible dup" : ""}
                  </Text>
                </View>
              )}
              {!!crawledAtStr && (
                <View style={styles.badge}>
                  <Text style={styles.badgeText}>crawled {crawledAtStr}</Text>
                </View>
              )}
            </View>

            {!!searchQuery && (
              <Text style={styles.provenanceMeta}>
                <Text style={styles.provenanceLabel}>Found via search: </Text>
                {searchQuery}
              </Text>
            )}

            {!!sourceUrl && (
              <View style={styles.provenanceLinkRow}>
                <Text style={styles.provenanceLabel}>Source: </Text>
                <TouchableOpacity onPress={() => openLink(sourceUrl)} style={styles.linkFlex}>
                  <Text style={styles.link} numberOfLines={1}>
                    {sourceUrl}
                  </Text>
                </TouchableOpacity>
              </View>
            )}

            {sourceUrls
              .filter((u) => u && u !== sourceUrl)
              .map((u) => (
                <TouchableOpacity key={u} onPress={() => openLink(u)}>
                  <Text style={[styles.link, styles.subLink]} numberOfLines={1}>
                    ↳ {u}
                  </Text>
                </TouchableOpacity>
              ))}

            {!!rawPretty && (
              <View style={styles.rawWrap}>
                <TouchableOpacity
                  style={styles.rawToggle}
                  onPress={() => setShowRaw((s) => !s)}
                  activeOpacity={0.7}
                >
                  <Ionicons
                    name={showRaw ? "chevron-down" : "chevron-forward"}
                    size={14}
                    color="#6B7280"
                  />
                  <Text style={styles.rawToggleText}>
                    {showRaw ? "Hide raw AI extract" : "Show raw AI extract"}
                  </Text>
                </TouchableOpacity>
                {showRaw && (
                  <ScrollView horizontal showsHorizontalScrollIndicator style={styles.rawBox}>
                    <Text style={styles.rawText} selectable>
                      {rawPretty}
                    </Text>
                  </ScrollView>
                )}
              </View>
            )}

            <Text style={styles.provenanceHint}>
              Fields below are what will be published. Edit anything before approving.
            </Text>
          </View>

          {/* Basic Information */}
          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Basic Information</Text>

            <Text style={styles.label}>Title*</Text>
            <TextInput
              style={styles.textInput}
              value={title}
              onChangeText={setTitle}
              placeholder="Enter opportunity title"
              placeholderTextColor="#9CA3AF"
            />

            <Text style={styles.label}>Organization*</Text>
            <TextInput
              style={styles.textInput}
              value={organization}
              onChangeText={setOrganization}
              placeholder="Enter organization name"
              placeholderTextColor="#9CA3AF"
            />

            <Text style={styles.label}>Type* (Select all that apply)</Text>
            <View style={styles.optionsContainer}>
              {opportunityTypes.map((t) => (
                <TouchableOpacity
                  key={t}
                  style={[styles.optionButton, types.includes(t) && styles.optionButtonSelected]}
                  onPress={() => toggleType(t)}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.optionText, types.includes(t) && styles.optionTextSelected]}>{t}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={styles.label}>Location*</Text>
            <TextInput
              style={styles.textInput}
              value={location}
              onChangeText={setLocation}
              placeholder="Enter location (city, online, etc.)"
              placeholderTextColor="#9CA3AF"
            />

            <Text style={styles.label}>Website</Text>
            {linkNeedsReview && (
              <View style={styles.linkWarn}>
                <Ionicons name="warning-outline" size={15} color="#B45309" />
                <Text style={styles.linkWarnText}>
                  Found on a roundup/list page — the crawler couldn&apos;t pin down this opportunity&apos;s
                  own link. Set the official URL below before publishing.
                </Text>
              </View>
            )}
            <TextInput
              style={[styles.textInput, linkNeedsReview && !website.trim() && styles.textInputWarn]}
              value={website}
              onChangeText={setWebsite}
              placeholder="Enter website URL"
              placeholderTextColor="#9CA3AF"
              keyboardType="url"
              autoCapitalize="none"
            />
            {!linkNeedsReview && !!website.trim() && (
              <View style={styles.linkNote}>
                <Ionicons
                  name={linkVerified ? "shield-checkmark-outline" : "information-circle-outline"}
                  size={14}
                  color={linkVerified ? "#059669" : "#6B7280"}
                />
                <Text style={[styles.linkNoteText, linkVerified && styles.linkNoteTextOk]}>
                  {linkVerified
                    ? "Verified — the crawler opened this page and confirmed it is this opportunity."
                    : "Not verified — the crawler could not confirm this page by fetching it."}
                  {linkResolution ? ` (${linkResolution})` : ""}
                </Text>
              </View>
            )}
            {showLinkCandidates && (
              <View style={styles.candidateWrap}>
                <Text style={styles.candidateLabel}>Suggested links from the page:</Text>
                {linkCandidates.map((u) => (
                  <View key={u} style={styles.candidateRow}>
                    <TouchableOpacity
                      style={[styles.candidateChip, website.trim() === u && styles.candidateChipActive]}
                      onPress={() => setWebsite(u)}
                      activeOpacity={0.7}
                    >
                      <Ionicons
                        name={website.trim() === u ? "checkmark-circle" : "add-circle-outline"}
                        size={14}
                        color={website.trim() === u ? "#059669" : "#2563EB"}
                      />
                      <Text style={styles.candidateChipText} numberOfLines={1}>
                        {u}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => openLink(u)} style={styles.candidateOpen}>
                      <Ionicons name="open-outline" size={14} color="#6B7280" />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            )}

            <Text style={styles.label}>Description*</Text>
            <TextInput
              style={styles.textAreaInput}
              value={description}
              onChangeText={setDescription}
              placeholder="Enter detailed description"
              placeholderTextColor="#9CA3AF"
              multiline
              numberOfLines={6}
              textAlignVertical="top"
            />
          </View>

          {/* Dates */}
          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Dates</Text>

            <Text style={styles.label}>Program/Event Dates (MM/DD/YYYY)</Text>
            <View style={styles.dateRow}>
              <View style={styles.dateInputContainer}>
                <Ionicons name="calendar-outline" size={20} color="#6B7280" />
                <TextInput
                  style={styles.dateInput}
                  value={startDate}
                  onChangeText={setStartDate}
                  placeholder="Start Date"
                  placeholderTextColor="#9CA3AF"
                />
              </View>
              <View style={styles.dateInputContainer}>
                <Ionicons name="calendar-outline" size={20} color="#6B7280" />
                <TextInput
                  style={styles.dateInput}
                  value={endDate}
                  onChangeText={setEndDate}
                  placeholder="End Date"
                  placeholderTextColor="#9CA3AF"
                />
              </View>
            </View>

            <Text style={styles.label}>Application Period (MM/DD/YYYY)</Text>
            <View style={styles.dateRow}>
              <View style={styles.dateInputContainer}>
                <Ionicons name="calendar-outline" size={20} color="#6B7280" />
                <TextInput
                  style={styles.dateInput}
                  value={applicationOpenDate}
                  onChangeText={setApplicationOpenDate}
                  placeholder="Opens"
                  placeholderTextColor="#9CA3AF"
                />
              </View>
              <View style={styles.dateInputContainer}>
                <Ionicons name="calendar-outline" size={20} color="#6B7280" />
                <TextInput
                  style={styles.dateInput}
                  value={applicationCloseDate}
                  onChangeText={setApplicationCloseDate}
                  placeholder="Closes"
                  placeholderTextColor="#9CA3AF"
                />
              </View>
            </View>

            <View style={styles.switchRow}>
              <Text style={styles.switchLabel}>Yearly Recurring Event</Text>
              <Switch
                value={isYearlyRecurring}
                onValueChange={setIsYearlyRecurring}
                trackColor={{ false: "#E5E7EB", true: "#DBEAFE" }}
                thumbColor={isYearlyRecurring ? "#2563EB" : "#FFFFFF"}
              />
            </View>
          </View>

          {/* Eligibility & Details */}
          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Eligibility &amp; Details</Text>

            <Text style={styles.label}>Grade Levels</Text>
            <View style={styles.optionsContainer}>
              {gradeOptions.map((g) => (
                <TouchableOpacity
                  key={g}
                  style={[styles.optionButton, gradeLevels.includes(g) && styles.optionButtonSelected]}
                  onPress={() => toggleGrade(g)}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.optionText, gradeLevels.includes(g) && styles.optionTextSelected]}>{g}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={styles.label}>Interaction Type</Text>
            <View style={styles.optionsContainer}>
              {interactionOptions.map((i) => (
                <TouchableOpacity
                  key={i}
                  style={[styles.optionButton, interactionTypes.includes(i) && styles.optionButtonSelected]}
                  onPress={() => toggleInteraction(i)}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.optionText, interactionTypes.includes(i) && styles.optionTextSelected]}>{i}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={styles.label}>Eligibility Requirements</Text>
            <TextInput
              style={styles.textAreaInput}
              value={eligibility}
              onChangeText={setEligibility}
              placeholder="Enter eligibility criteria"
              placeholderTextColor="#9CA3AF"
              multiline
              numberOfLines={4}
              textAlignVertical="top"
            />

            <Text style={styles.label}>Cost</Text>
            <TextInput
              style={styles.textInput}
              value={cost}
              onChangeText={setCost}
              placeholder="Enter cost information (e.g., Free, $50, etc.)"
              placeholderTextColor="#9CA3AF"
            />

            <Text style={styles.label}>Tags</Text>
            <View style={styles.tagInputContainer}>
              <TextInput
                style={styles.tagInput}
                value={tagInput}
                onChangeText={setTagInput}
                placeholder="Add tags"
                placeholderTextColor="#9CA3AF"
                onSubmitEditing={addTag}
              />
              <TouchableOpacity style={styles.addTagButton} onPress={addTag}>
                <Ionicons name="add" size={20} color="#FFFFFF" />
              </TouchableOpacity>
            </View>
            <View style={styles.tagsContainer}>
              {tags.map((tag) => (
                <View key={tag} style={styles.tag}>
                  <Text style={styles.tagText}>{tag}</Text>
                  <TouchableOpacity onPress={() => removeTag(tag)}>
                    <Ionicons name="close-circle" size={16} color="#6B7280" />
                  </TouchableOpacity>
                </View>
              ))}
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    </View>
  );
};

const styles = StyleSheet.create({
  fullScreenModal: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#F9FAFB",
    zIndex: 1000,
  },
  container: {
    flex: 1,
    backgroundColor: "#F9FAFB",
  },

  // Header
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    marginRight: 12,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: "#F9FAFB",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  headerTitleContainer: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "600",
    color: "#111827",
    fontFamily: "Inter-SemiBold",
  },
  headerSubtitle: {
    fontSize: 14,
    color: "#6B7280",
    fontFamily: "Inter-Regular",
    marginTop: 2,
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
  },
  rejectButton: {
    backgroundColor: "#FEF2F2",
    borderWidth: 1,
    borderColor: "#FECACA",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    marginRight: 12,
    minWidth: 76,
    alignItems: "center",
    justifyContent: "center",
  },
  rejectButtonText: {
    fontSize: 14,
    color: "#DC2626",
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  approveButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#10B981",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    minWidth: 150,
  },
  approveButtonText: {
    fontSize: 14,
    color: "#FFFFFF",
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
    marginLeft: 4,
  },
  disabledButton: {
    opacity: 0.6,
  },

  // Content
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
    paddingBottom: 40,
  },

  // Provenance
  provenanceSection: {
    marginBottom: 24,
    backgroundColor: "#F5F3FF",
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: "#DDD6FE",
  },
  provenanceHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
  },
  provenanceTitle: {
    fontSize: 15,
    fontWeight: "600",
    color: "#5B21B6",
    fontFamily: "Inter-SemiBold",
    marginLeft: 6,
  },
  provenanceMeta: {
    fontSize: 13,
    color: "#4A5568",
    marginTop: 8,
    fontFamily: "Inter-Regular",
  },
  provenanceLabel: {
    fontWeight: "600",
    color: "#374151",
    fontFamily: "Inter-SemiBold",
  },
  provenanceLinkRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 8,
  },
  linkFlex: {
    flex: 1,
  },
  provenanceHint: {
    fontSize: 12,
    color: "#7C3AED",
    marginTop: 14,
    fontFamily: "Inter-Medium",
  },

  // Badges
  badges: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  badge: {
    backgroundColor: "#EDE9FE",
    borderRadius: 6,
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  badgeText: {
    fontSize: 12,
    color: "#5B21B6",
    fontFamily: "Inter-Medium",
  },
  badgeWarn: {
    backgroundColor: "#FEF2F2",
  },
  badgeWarnText: {
    color: "#DC2626",
  },
  link: {
    fontSize: 13,
    color: "#3182CE",
    fontFamily: "Inter-Regular",
  },
  subLink: {
    marginTop: 6,
    color: "#6B7280",
  },

  // Raw extract
  rawWrap: {
    marginTop: 12,
  },
  rawToggle: {
    flexDirection: "row",
    alignItems: "center",
  },
  rawToggleText: {
    fontSize: 13,
    color: "#6B7280",
    fontFamily: "Inter-Medium",
    marginLeft: 4,
  },
  rawBox: {
    marginTop: 8,
    backgroundColor: "#1E1B2E",
    borderRadius: 8,
    padding: 12,
    maxHeight: 240,
  },
  rawText: {
    fontSize: 12,
    color: "#E5E7EB",
    fontFamily: Platform.OS === "ios" ? "Menlo" : "monospace",
  },

  // Form sections (mirrors OpportunityEditorScreen)
  formSection: {
    marginBottom: 24,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#111827",
    marginBottom: 20,
    fontFamily: "Inter-SemiBold",
  },
  label: {
    fontSize: 14,
    fontWeight: "500",
    color: "#374151",
    marginBottom: 8,
    fontFamily: "Inter-Medium",
  },
  textInput: {
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: "#111827",
    marginBottom: 16,
    fontFamily: "Inter-Regular",
  },
  textInputWarn: {
    borderColor: "#F59E0B",
    backgroundColor: "#FFFBEB",
  },
  linkWarn: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 6,
    backgroundColor: "#FFFBEB",
    borderColor: "#FDE68A",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginBottom: 8,
  },
  linkWarnText: {
    flex: 1,
    fontSize: 12,
    lineHeight: 17,
    color: "#92400E",
    fontFamily: "Inter-Regular",
  },
  linkNote: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 6,
    marginTop: -10,
    marginBottom: 14,
  },
  linkNoteText: {
    flex: 1,
    fontSize: 11,
    lineHeight: 16,
    color: "#6B7280",
    fontFamily: "Inter-Regular",
  },
  linkNoteTextOk: {
    color: "#047857",
  },
  candidateWrap: {
    marginTop: -8,
    marginBottom: 16,
  },
  candidateLabel: {
    fontSize: 12,
    color: "#6B7280",
    marginBottom: 6,
    fontFamily: "Inter-Medium",
  },
  candidateRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 6,
  },
  candidateChip: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: "#EFF6FF",
    borderColor: "#BFDBFE",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  candidateChipActive: {
    backgroundColor: "#ECFDF5",
    borderColor: "#A7F3D0",
  },
  candidateChipText: {
    flex: 1,
    fontSize: 12,
    color: "#1F2937",
    fontFamily: "Inter-Regular",
  },
  candidateOpen: {
    padding: 6,
  },
  textAreaInput: {
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: "#111827",
    marginBottom: 16,
    minHeight: 100,
    fontFamily: "Inter-Regular",
  },
  optionsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 16,
    marginHorizontal: -4,
  },
  optionButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    backgroundColor: "#F9FAFB",
    marginHorizontal: 4,
    marginBottom: 8,
  },
  optionButtonSelected: {
    backgroundColor: "#2563EB",
    borderColor: "#2563EB",
  },
  optionText: {
    color: "#6B7280",
    fontSize: 14,
    fontFamily: "Inter-Medium",
  },
  optionTextSelected: {
    color: "#FFFFFF",
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  dateRow: {
    flexDirection: "row",
    marginBottom: 16,
    marginHorizontal: -6,
  },
  dateInputContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginHorizontal: 6,
  },
  dateInput: {
    flex: 1,
    fontSize: 16,
    color: "#111827",
    marginLeft: 8,
    fontFamily: "Inter-Regular",
  },
  switchRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginVertical: 8,
  },
  switchLabel: {
    fontSize: 16,
    color: "#374151",
    fontFamily: "Inter-Regular",
  },
  tagInputContainer: {
    flexDirection: "row",
    marginBottom: 12,
  },
  tagInput: {
    flex: 1,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: "#111827",
    marginRight: 8,
    fontFamily: "Inter-Regular",
  },
  addTagButton: {
    backgroundColor: "#2563EB",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  tagsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 4,
    marginHorizontal: -2,
  },
  tag: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#EEF2FF",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginHorizontal: 2,
    marginBottom: 4,
  },
  tagText: {
    fontSize: 12,
    color: "#2563EB",
    marginRight: 4,
    fontFamily: "Inter-Medium",
  },
});

export default CrawledOpportunityEditor;
